<div class="space-y-4 md:space-y-6">
  @if (loading) {
    <div class="rounded-2xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500 md:px-6 md:py-12">
      Loading guests…
    </div>
  } @else if (loadError) {
    <div class="rounded-2xl border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-700 md:px-6">
      {{ loadError }}
    </div>
  } @else {
    <input #guestFileInput type="file" accept=".csv,.xlsx,.xls" class="hidden" (change)="onImportFileSelected($event)">
    <div class="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div class="space-y-2">
            <h3 class="text-xl font-semibold text-gray-900">Guests &amp; Dietary Requirements</h3>
            <p class="text-sm text-gray-500">
              Upload your guest list, and don&apos;t forget yourselves.
            </p>
          </div>
          <div class="flex flex-wrap gap-2 lg:shrink-0">
            @if (stage === 'list' && permissions.can_import) {
              <!-- TVG-270: upload a first name / last name spreadsheet (xlsx or csv). -->
              <app-button [label]="'Download template'" [style]="'secondary'" [icon]="DownloadIcon" (click)="downloadTemplate()"></app-button>
              <app-button [label]="'Upload spreadsheet'" [style]="'secondary'" [icon]="FileUpIcon" (click)="triggerFilePicker(guestFileInput)"></app-button>
            }
            @if (stage === 'list' && permissions.can_manage_guests) {
              <app-button [label]="'Add guest'" [style]="'secondary'" [icon]="PlusIcon" (click)="openGuestModal()"></app-button>
            }
          </div>
        </div>
      </div>
    </div>

    @if (stage === 'upload') {
      <div class="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-10 md:px-8 md:py-12">
        <div class="mx-auto max-w-2xl text-center">
          <button
            type="button"
            class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600 transition hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-60"
            [disabled]="!permissions.can_manage_guests"
            (click)="openGuestModal()"
            aria-label="Add guest"
          >
            <lucide-icon [name]="PlusIcon" size="24"></lucide-icon>
          </button>
          <h4 class="mt-5 text-2xl font-semibold text-gray-900">Add your first guest</h4>
          <p class="mt-3 text-sm leading-6 text-gray-500">
            Start building the guest list manually, or upload a spreadsheet of first and last names. You can capture menu choices, dietary details, and scanned dietary forms once guests are in place.
          </p>

          <div class="mt-8 flex flex-wrap items-center justify-center gap-3">
            @if (permissions.can_manage_guests) {
              <app-button [label]="'Add guest'" [style]="'secondary'" [icon]="PlusIcon" (click)="openGuestModal()"></app-button>
            }
            @if (permissions.can_import) {
              <!-- TVG-270: upload a first name / last name spreadsheet (xlsx or csv). -->
              <app-button [label]="'Upload spreadsheet'" [style]="'primary'" [icon]="FileUpIcon" (click)="triggerFilePicker(guestFileInput)"></app-button>
              <app-button [label]="'Download template'" [style]="'secondary'" [icon]="DownloadIcon" (click)="downloadTemplate()"></app-button>
            }
          </div>

          @if (importPreviewLoading) {
            <div class="mt-8 text-sm text-gray-500">Reading spreadsheet and preparing preview…</div>
          }
        </div>
      </div>
    }

    @if (stage === 'list') {
      <div class="grid grid-cols-2 gap-3 md:gap-4 md:grid-cols-3 xl:grid-cols-5">
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Day guests</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.total_day_guests }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Evening guests</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.total_evening_guests }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Adults</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.adults }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Teens</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.teens }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Children</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.children }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Meat eaters</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.meat_eaters }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Vegetarians &amp; vegans</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.vegetarians_and_vegans }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Guests with dietaries</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.with_requirements }}</div>
        </div>
        <div class="flex min-h-28 flex-col justify-between rounded-2xl border border-gray-200 bg-white px-4 py-4 md:min-h-32 md:px-5">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Forms uploaded</div>
          <div class="text-2xl font-semibold text-gray-900 md:text-3xl">{{ summary.forms_uploaded }}</div>
        </div>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div class="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
            <div class="relative w-full md:max-w-sm">
              <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                <lucide-icon [name]="SearchIcon" size="16"></lucide-icon>
              </span>
              <input
                [formControl]="searchControl"
                type="text"
                placeholder="Search guests…"
                class="h-11 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10"
              >
            </div>
            <label class="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" [formControl]="showDietaryOnlyControl" class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
              Show dietary only
            </label>
          </div>
          <div class="min-w-52">
            <app-select
              [control]="attendanceFilterControl"
              [label]="''"
              [choices]="attendanceChoices()"
              [placeholder]="'All guest types'"
            ></app-select>
          </div>
        </div>

        @if (permissions.can_manage_guests && hasSelectedGuests()) {
          <div class="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div class="text-sm font-medium text-amber-900">{{ selectedGuestCount() }} guests selected</div>
            <div class="flex flex-wrap gap-2">
              <app-button [label]="'Mark as accommodation'" [style]="'secondary'" (click)="markSelectedAccommodation()"></app-button>
              <button type="button" class="text-sm font-medium text-amber-700 transition hover:text-amber-900" (click)="clearSelection()">Clear selection</button>
            </div>
          </div>
        }

        <div class="mt-5">
          <app-data-table
            [dataPage]="guestTablePage()"
            [columns]="guestTableColumns"
            [rowActions]="guestRowActions"
            [paginate]="false"
            [mobileCardView]="true"
            [mobileCompact]="mobileMinimal"
            [mobileFlatShell]="true"
            [flatShell]="true"
            [hover]="true"
            [rowClickable]="true"
            [selectionEnabled]="permissions.can_manage_guests"
            [selectedRowIds]="selectedGuestIds"
            [selectedRowId]="expandedGuestId ?? undefined"
            [selectedRowClass]="'!bg-brand-50 dark:!bg-brand-900/20'"
            [expandedRowTemplate]="guestDietaryDetailTemplate"
            (rowClick)="toggleExpandedGuest($event)"
            (selectionChange)="toggleGuestSelection($event.item.id, $event.checked)"
          ></app-data-table>
        </div>
      </div>
    }
  }
</div>

<app-modal
  [show]="showImportPreview"
  [title]="importResult ? 'Guest import summary' : 'Review guest import before saving'"
  [confirm]="!importResult"
  [confirmButtonLabel]="'Import ' + importableCount() + (importableCount() === 1 ? ' guest' : ' guests')"
  [confirmDisabled]="importableCount() === 0"
  [closeButtonLabel]="importResult ? 'Done' : 'Cancel'"
  [processing]="importConfirming"
  [size]="'wide'"
  (onClose)="cancelImportPreview()"
  (onConfirm)="confirmImport()"
>
  @if (importResult) {
    <!-- TVG-270: on-screen summary of what was added, skipped and rejected. -->
    <div class="space-y-6">
      <div class="flex flex-wrap gap-2">
        <span class="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">Added {{ importResult.summary.created }}</span>
        <span class="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">Skipped duplicates {{ importResult.summary.skipped_duplicates }}</span>
        <span class="rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">Invalid {{ importResult.summary.invalid }}</span>
      </div>

      @if (importResult.results.skipped_duplicates.length || importResult.results.invalid.length) {
        <div class="overflow-hidden rounded-2xl border border-gray-200">
          <div class="overflow-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th class="px-4 py-3 font-semibold">First Name</th>
                  <th class="px-4 py-3 font-semibold">Last Name</th>
                  <th class="px-4 py-3 font-semibold">Result</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                @for (row of importResult.results.skipped_duplicates; track 'dup-' + row.row) {
                  <tr class="align-top">
                    <td class="px-4 py-3 text-gray-900">{{ row.first_name || '—' }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ row.last_name || '—' }}</td>
                    <td class="px-4 py-3">
                      <div class="flex flex-col gap-1">
                        <span class="inline-flex w-fit items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">Skipped duplicate</span>
                        <span class="text-xs text-gray-500">Not confirmed for import.</span>
                      </div>
                    </td>
                  </tr>
                }
                @for (row of importResult.results.invalid; track 'inv-' + row.row) {
                  <tr class="align-top">
                    <td class="px-4 py-3 text-gray-900">{{ row.first_name || '—' }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ row.last_name || '—' }}</td>
                    <td class="px-4 py-3">
                      <div class="flex flex-col gap-1">
                        <span class="inline-flex w-fit items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">Invalid</span>
                        @if (row.message) {
                          <span class="text-xs text-gray-500">{{ row.message }}</span>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      } @else {
        <p class="text-sm text-gray-500">All {{ importResult.summary.created }} guests were added successfully.</p>
      }
    </div>
  } @else if (importPreview) {
    <div class="space-y-6">
      <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div class="text-xs font-semibold uppercase tracking-[0.24em] text-brand-500">Preview &amp; confirm</div>
          <h4 class="mt-2 text-lg font-semibold text-gray-900">Review guest import before saving</h4>
          <p class="mt-1 text-sm text-gray-500">Only first and last name are imported. Age group and type of guest are added afterwards.</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <span class="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600">Total {{ importPreview.summary.total }}</span>
          <span class="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">New {{ importPreview.summary.new_count }}</span>
          <span class="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">Possible duplicates {{ importPreview.summary.duplicate_count }}</span>
          @if (importPreview.summary.invalid_count) {
            <span class="rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">Invalid {{ importPreview.summary.invalid_count }}</span>
          }
        </div>
      </div>

      @if (importPreview.summary.duplicate_count) {
        <div class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Some names already appear on the list or more than once in the file. Tick &ldquo;Add anyway&rdquo; to import them as new guests.
        </div>
      }

      <div class="overflow-hidden rounded-2xl border border-gray-200">
        <div class="overflow-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 text-left text-gray-600">
              <tr>
                <th class="px-4 py-3 font-semibold">First Name</th>
                <th class="px-4 py-3 font-semibold">Last Name</th>
                <th class="px-4 py-3 font-semibold">Status</th>
                <th class="px-4 py-3 font-semibold text-center">Add anyway</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 bg-white">
              @for (row of importPreview.rows; track row.row) {
                <tr class="align-top">
                  <td class="px-4 py-3 text-gray-900">{{ row.first_name || '—' }}</td>
                  <td class="px-4 py-3 text-gray-900">{{ row.last_name || '—' }}</td>
                  <td class="px-4 py-3">
                    <div class="flex flex-col gap-1">
                      <span class="inline-flex w-fit items-center rounded-full px-2.5 py-1 text-xs font-semibold" [ngClass]="statusBadgeClass(row.status)">
                        {{ statusLabel(row.status) }}
                      </span>
                      @if (row.message) {
                        <span class="text-xs text-gray-500">{{ row.message }}</span>
                      }
                    </div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    @if (row.status === 'duplicate') {
                      <input
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                        [checked]="isDuplicateConfirmed(row.row)"
                        (change)="toggleDuplicateConfirm(row.row, $any($event.target).checked)"
                        [attr.aria-label]="'Add ' + row.first_name + ' ' + row.last_name + ' anyway'"
                      >
                    } @else {
                      <span class="text-gray-300">—</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  } @else {
    <p class="text-sm text-gray-500">No preview data available.</p>
  }
</app-modal>

<app-modal
  [show]="importPreviewLoading && !showImportPreview"
  [title]="'Processing guest file…'"
  [confirm]="false"
  [showFooter]="false"
  [processing]="true"
>
  <div class="flex flex-col items-center justify-center gap-4 py-10">
    <div class="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500"></div>
    <div class="text-sm text-gray-500">
      Reading spreadsheet and preparing preview…
    </div>
  </div>
</app-modal>

<ng-template #guestDietaryDetailTemplate let-guest>
  @if (guest.dietary) {
    <div class="rounded-xl border border-gray-200 bg-gray-50/70 px-5 py-4">
      <div class="mb-4">
        <div class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Dietary details</div>
        <div class="mt-1 text-sm font-semibold text-gray-900">{{ guest.full_name }}</div>
      </div>

      <div class="grid gap-x-8 gap-y-4 md:grid-cols-2 xl:grid-cols-4">
        <div class="min-w-0">
          <div class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Menu</div>
          <div class="mt-1 text-sm text-gray-700">{{ guest.dietary.menu_preference || '—' }}</div>
        </div>
        <div class="min-w-0">
          <div class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Allergens</div>
          <div class="mt-1 text-sm text-gray-700">{{ (guest.dietary.allergens || []).join(', ') || '—' }}</div>
        </div>
        <div class="min-w-0">
          <div class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Religious / lifestyle</div>
          <div class="mt-1 text-sm text-gray-700">{{ guest.dietary.religious_details || (guest.dietary.religious_or_lifestyle ? 'Yes' : '—') }}</div>
        </div>
        <div class="min-w-0">
          <div class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Flags</div>
          <div class="mt-1 text-sm text-gray-700">
            @if (guest.dietary.airborne_allergy || guest.dietary.religious_or_lifestyle) {
              <span>{{ guest.dietary.airborne_allergy ? 'Airborne allergy' : '' }}</span>
              <span>{{ guest.dietary.airborne_allergy && guest.dietary.religious_or_lifestyle ? ' · ' : '' }}</span>
              <span>{{ guest.dietary.religious_or_lifestyle ? 'Religious / lifestyle' : '' }}</span>
            } @else {
              —
            }
          </div>
        </div>
        <div class="min-w-0 xl:col-span-2">
          <div class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Any other information</div>
          <div class="mt-1 text-sm text-gray-700">{{ guest.dietary.additional_notes || '—' }}</div>
        </div>
        <div class="min-w-0 xl:col-span-2">
          <div class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Scanned forms</div>
          <div class="mt-1 flex flex-col gap-2 text-sm text-gray-700">
            @if (guest.dietary.attachments.length) {
              @for (attachment of guest.dietary.attachments; track attachment.id) {
                <a [href]="attachment.url || '#'" target="_blank" rel="noreferrer" class="inline-flex w-fit items-center gap-2 text-brand-600 hover:text-brand-700">
                  <lucide-icon [name]="DownloadIcon" size="14"></lucide-icon>
                  {{ attachment.original_name }}
                </a>
              }
            } @else {
              <span>—</span>
            }
          </div>
        </div>
      </div>
    </div>
  } @else {
    <div class="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-5 py-6">
      <div class="text-center text-sm font-semibold text-gray-900">No dietary details recorded</div>
    </div>
  }
</ng-template>

<app-modal
  [show]="showGuestModal"
  [title]="editingGuest ? 'Edit Guest' : 'Add Guest'"
  [confirmButtonLabel]="editingGuest ? 'Save Guest' : 'Add Guest'"
  [processing]="savingGuest"
  [confirmDisabled]="savingGuest"
  [tall]="true"
  [allowOverflow]="true"
  (onClose)="closeGuestModal()"
  (onConfirm)="saveGuest()"
>
  <div class="space-y-6">
    <div class="grid gap-5 sm:grid-cols-2">
      <app-text-input [control]="guestForm.controls.first_name" [label]="'First name'" [name]="'first_name'"></app-text-input>
      <app-text-input [control]="guestForm.controls.last_name" [label]="'Last name'" [name]="'last_name'"></app-text-input>
    </div>

    <div class="grid gap-5 sm:grid-cols-2">
      <app-text-input
        [control]="guestForm.controls.email"
        [label]="'Email'"
        [name]="'email'"
        [type]="'email'"
        [helpText]="'Optional.'"
      ></app-text-input>
      <app-text-input
        [control]="guestForm.controls.mobile"
        [label]="'Mobile number'"
        [name]="'mobile'"
        [helpText]="'Optional.'"
      ></app-text-input>
    </div>

    <div class="grid gap-5 sm:grid-cols-2">
      <app-select
        [control]="guestForm.controls.guest_type"
        [label]="'Age Group'"
        [choices]="guestTypeChoices()"
        [placeholder]="'Please Select'"
        [required]="true"
        [clearable]="false"
      ></app-select>
      <app-select
        [control]="guestForm.controls.attendance"
        [label]="'Type of Guest'"
        [choices]="attendanceChoices()"
        [placeholder]="'Please Select'"
        [required]="true"
        [clearable]="false"
      ></app-select>
    </div>

    <app-select
      [control]="guestForm.controls.menu_preference"
      [label]="'Menu Type'"
      [choices]="menuPreferenceChoices()"
      [placeholder]="'Select menu type'"
    ></app-select>

    <label class="inline-flex items-center gap-3 pt-2 text-sm font-medium text-gray-700">
      <input type="checkbox" [formControl]="guestForm.controls.requires_accommodation" class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
      Guest requires overnight accommodation
    </label>

    @if (highChairEnabled) {
      <label class="inline-flex items-center gap-3 text-sm font-medium text-gray-700">
        <input type="checkbox" [formControl]="guestForm.controls.requires_high_chair" class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
        Guest needs a high chair
      </label>
    }
  </div>
</app-modal>

<app-modal
  [show]="showDietaryModal"
  [title]="'Edit Dietary Details'"
  [confirmButtonLabel]="'Save Dietary Details'"
  [processing]="savingDietary"
  [confirmDisabled]="!canSaveDietary()"
  [size]="'wide'"
  (onClose)="closeDietaryModal()"
  (onConfirm)="saveDietary()"
>
  <div
    class="space-y-5 transition-all duration-200 ease-out"
    [ngClass]="{
      'opacity-65 saturate-75': savingDietary
    }"
  >
    <input #dietaryAttachmentInput type="file" accept=".pdf,.png,.jpg,.jpeg,.gif" multiple class="hidden" (change)="onDietaryFilesSelected($event)">

    <div class="border border-amber-200 bg-amber-50 px-4 py-4 text-sm font-medium text-amber-800">
      Dietary requirements must be submitted on a signed wet ink form by each guest. Electronic signatures cannot be accepted.
    </div>

    <div>
      <app-select
        [control]="dietaryForm.controls.event_guest_id"
        [label]="'Guest'"
        [choices]="guestSelectChoices()"
        [placeholder]="'Select guest'"
        [searchable]="true"
      ></app-select>
    </div>

    <div class="border-t border-gray-200"></div>

    <div>
      @if (dietaryFormError) {
        <div class="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 transition-all duration-200 ease-out">
          {{ dietaryFormError }}
        </div>
      } @else if (dietarySaveSuccess) {
        <div class="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 transition-all duration-200 ease-out">
          Dietary details saved successfully.
        </div>
      }
    </div>

    <div>
      <app-select
        [control]="dietaryForm.controls.menu_preference"
        [label]="'Menu Type'"
        [choices]="menuPreferenceChoices()"
        [placeholder]="'Select menu type'"
      ></app-select>
    </div>

    <div class="space-y-1.5">
      <div class="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Allergens</div>
      <div class="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-4">
        @for (allergen of choices.allergens; track allergen) {
          <label class="inline-flex items-center gap-2 py-0.5 text-sm text-gray-700">
            <input
              type="checkbox"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              [checked]="multiSelected(dietaryForm.controls.allergens, allergen)"
              (change)="toggleMultiChoice(dietaryForm.controls.allergens, allergen, $any($event.target).checked)"
            >
            {{ allergen }}
          </label>
        }
      </div>
    </div>

    <div class="grid gap-4 md:grid-cols-3">
      <div class="space-y-1.5">
        <div class="text-sm font-medium text-gray-700">Airborne allergy?</div>
        <div class="flex flex-wrap gap-2">
          <label class="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700">
            <input type="radio" name="dietary_airborne_allergy" [value]="true" [formControl]="dietaryForm.controls.airborne_allergy" class="h-4 w-4 border-gray-300 text-brand-500 focus:ring-brand-500">
            Yes
          </label>
          <label class="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700">
            <input type="radio" name="dietary_airborne_allergy" [value]="false" [formControl]="dietaryForm.controls.airborne_allergy" class="h-4 w-4 border-gray-300 text-brand-500 focus:ring-brand-500">
            No
          </label>
        </div>
      </div>

      <div class="space-y-1.5">
        <div class="text-sm font-medium text-gray-700">Religious dietary requirements?</div>
        <div class="flex flex-wrap gap-2">
          <label class="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700">
            <input type="radio" name="dietary_religious_or_lifestyle" [value]="true" [formControl]="dietaryForm.controls.religious_or_lifestyle" class="h-4 w-4 border-gray-300 text-brand-500 focus:ring-brand-500">
            Yes
          </label>
          <label class="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700">
            <input type="radio" name="dietary_religious_or_lifestyle" [value]="false" [formControl]="dietaryForm.controls.religious_or_lifestyle" class="h-4 w-4 border-gray-300 text-brand-500 focus:ring-brand-500">
            No
          </label>
        </div>
      </div>

      <app-text-input
        [control]="dietaryForm.controls.religious_details"
        [label]="'If yes, please state'"
        [name]="'religious_details'"
        [placeholder]="'e.g. Halal, Kosher'"
      ></app-text-input>
    </div>

    <div>
      <app-textarea [control]="dietaryForm.controls.additional_notes" [label]="'Any other information'"></app-textarea>
    </div>

    <div class="space-y-4 rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
      <div class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Scanned dietary forms</div>

      @if ((dietaryGuest?.dietary?.attachments?.length ?? 0) > 0) {
        <div class="space-y-2">
          @for (attachment of dietaryGuest!.dietary!.attachments; track attachment.id) {
            <div class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
              <div class="min-w-0">
                <div class="truncate text-sm font-medium text-gray-900">{{ attachment.original_name }}</div>
                @if (isAttachmentMarkedForRemoval(attachment.id)) {
                  <div class="text-xs text-red-600">Will be removed when you save.</div>
                }
              </div>
              <div class="flex items-center gap-2">
                @if (attachment.url) {
                  <a [href]="attachment.url" target="_blank" rel="noreferrer" class="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700">
                    <lucide-icon [name]="DownloadIcon" size="14"></lucide-icon>
                    Download
                  </a>
                }
                <button
                  type="button"
                  class="text-sm font-medium"
                  [ngClass]="isAttachmentMarkedForRemoval(attachment.id) ? 'text-emerald-700 hover:text-emerald-800' : 'text-red-600 hover:text-red-700'"
                  (click)="toggleAttachmentRemoval(attachment.id)"
                >
                  {{ isAttachmentMarkedForRemoval(attachment.id) ? 'Undo' : 'Remove' }}
                </button>
              </div>
            </div>
          }
        </div>
      }

      @if (pendingDietaryFiles.length > 0) {
        <div class="space-y-2">
          @for (file of pendingDietaryFiles; track file.name + file.size + $index) {
            <div class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-brand-200 bg-brand-50/50 px-4 py-3">
              <div class="min-w-0">
                <div class="truncate text-sm font-medium text-gray-900">{{ file.name }}</div>
                <div class="text-xs text-gray-500">Will upload when you save.</div>
              </div>
              <button type="button" class="text-sm font-medium text-red-600 hover:text-red-700" (click)="removePendingDietaryFile($index)">
                Remove
              </button>
            </div>
          }
        </div>
      }

      @if ((dietaryGuest?.dietary?.attachments?.length ?? 0) === 0 && pendingDietaryFiles.length === 0) {
        <div class="text-sm text-gray-500">No scanned dietary forms uploaded yet.</div>
      }
    </div>
  </div>

  <button
    modal-footer-left
    type="button"
    class="inline-flex h-12 items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 text-sm font-medium text-gray-700 transition-all duration-200 ease-out hover:border-brand-300 hover:text-brand-600"
    [disabled]="savingDietary"
    [ngClass]="{
      'opacity-50 cursor-not-allowed': savingDietary
    }"
    (click)="triggerFilePicker(dietaryAttachmentInput)"
  >
    <lucide-icon [name]="FileUpIcon" size="16"></lucide-icon>
    Add files
  </button>
</app-modal>
